import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {
    Button, Chip, Box, List, Fade, Checkbox, Paper, Avatar, ListItemButton, ListItemAvatar,
    ToggleButtonGroup, ToggleButton, Tab, Tabs, InputAdornment,
    Dialog,
    DialogActions,
    DialogContent,
    FormControlLabel, Grow, ListItem, ListSubheader,
    Switch, TextField,
    Typography,
    useMediaQuery,
    useTheme, InputLabel, Select, MenuItem, FormControl, Autocomplete, CircularProgress
} from "@mui/material";
import {
    PersonAdd as PersonAddIcon,
    History as HistoryIcon,
    Group as GroupIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    AddCircle as AddCircleIcon,
    CheckCircle as CheckCircleIcon,
    ContactPhone as ContactPhoneIcon,
    Search as SearchIcon,
    Login as LoginIcon,
    InfoOutlined as InfoIcon,
    Devices as DevicesIcon,
    CameraAlt as CameraAltIcon,
    AutoAwesome as AutoAwesomeIcon,
    MarkEmailRead as MarkEmailReadIcon
} from '@mui/icons-material';
import IconButton from "@mui/material/IconButton";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import {PAContext} from "../../../Services/PAContext";
import {useGoogleAutocompleteUniversal} from "../../Helpers/GoogleAutocomplete";
import {getAllContactsFromDatabase} from "../../Restricted/Contacts/BrowserDatabaseFunctions";
import ListItemText from "@mui/material/ListItemText";
import LockIcon from "@mui/icons-material/Lock";
import country_codes from "../../../sampleData/PhonePrefixes";
import {supportedLanguages} from "../../../sampleData/PreferredLanguages";
import {_airports_list} from "../../../sampleData/airline_data";
import PAAttendee from "../../../MasterEventObjects/Attendee";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";




function AttendeesAddDialog({ isOpen, onClose }){

    const inputRef = useRef(null);

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();

    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account, SampleAttendees, authenticated } = dataArray;

    const [dbContacts, setDbContacts] = useState([]);
    useEffect(() => {
        if (!authenticated) {
            setDbContacts([]);
            return;
        }

        let cancelled = false;

        getAllContactsFromDatabase()
            .then(contacts => {
                if (cancelled) return;
                setDbContacts(Array.isArray(contacts) ? contacts : []);
            })
            .catch(err => {
                console.error('Failed to load Contacts from browser DB:', err);
                if (!cancelled) {
                    setDbContacts([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [authenticated]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [activeTab, setActiveTab] = useState(0);
    const [selectedAttendees, setSelectedAttendees] = useState([]);

    // Search State for Previous Tab
    const [searchQuery, setSearchQuery] = useState('');
    const [searchCriteria, setSearchCriteria] = useState('first'); // 'first' or 'last'

    // Detection state + Debug override
    const [isAndroidChrome, setIsAndroidChrome] = useState(false);
    const [debugOverride, setDebugOverride] = useState(false);

    const canAccessContacts = isAndroidChrome || debugOverride;

    const [editingId, setEditingId] = useState(null);
    const [manualEntry, setManualEntry] = useState({
        avatar: null, fname: '', lname: '', nickname: '', isNicknameDirty: false,
        email: '', phone_prefix: '+1', phone: '',
        address: '', city: '', state: '', zip: '', country: 'US',
        dob: '', language: 'en', preferred_airport: ''
    });

    const handleManualChange = (field, value) => {
        setManualEntry(prev => {
            let newState = { ...prev, [field]: value };

            // Logic: Sync nickname to fname if nickname hasn't been manually edited
            if (field === 'fname' && !prev.isNicknameDirty) {
                newState.nickname = value;
            }

            // Logic: If user types in nickname, mark it as "Dirty" (Override active)
            if (field === 'nickname') {
                newState.isNicknameDirty = value.length > 0;
            }

            return newState;
        });
    };

    // Email Logic State
    const [sendRSVP, setSendRSVP] = useState(true);
    const [sendWelcome, setSendWelcome] = useState(false);
    const [customMessage, setCustomMessage] = useState('');

    // Browser/OS Detection for Contact API
    useEffect(() => {
        const ua = navigator.userAgent;
        const isAndroid = /Android/i.test(ua);
        const isChrome = /Chrome/i.test(ua) && !/Edge/i.test(ua);
        const hasContactAPI = 'contacts' in navigator && 'ContactsManager' in window;


        if (isAndroid && isChrome && hasContactAPI) {
            setIsAndroidChrome(true);
        }

        setSelectedAttendees([]);

        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current);
        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            console.log(JSON.stringify(place, null, '\t'));
            console.log(place.formatted_address);

            let _address_line = "";
            let _address_city = "";
            let _address_state = "";
            let _address_zip = "";
            let _address_country = "";

            let _route_exists = false;
            let _country_code = "";
            for (const component of place.address_components){
                if (component.types.includes("route")) {
                    _route_exists = true;
                } else if (component.types.includes("country")){
                    _country_code = component.short_name;
                }
            }

            place.address_components.forEach((component) => {
                if (component.types.includes("street_number")){
                    _address_line = component.long_name;
                } else if (component.types.includes("route")){
                    _address_line += " "+component.long_name;
                } else if (!_route_exists && component.types.includes("administrative_area_level_2")){
                    _address_line += " "+component.long_name;
                } else if (component.types.includes("locality") || component.types.includes("postal_town")){
                    _address_city = component.long_name;
                } else if (component.types.includes("administrative_area_level_1") && _country_code === "US"){
                    _address_state = component.short_name;
                } else if (component.types.includes("postal_code")){
                    _address_zip = component.long_name;
                } else if (component.types.includes("country")){
                    if (_country_code !== "US"){
                        _address_state = component.long_name;
                    }
                    _address_country = component.short_name;
                }
            });

            if (_address_line === ""){
                //try getting it from the formatted address
                try {
                    const _f_address_pieces = place.formatted_address.split(',');
                    if (_f_address_pieces[0] !== ""){
                        _address_line = _f_address_pieces[0].trim();
                    }
                } catch (e){}
            }

            console.log("the _address_line : "+_address_line);
            console.log("the _address_city : "+_address_city);
            console.log("the _address_state : "+_address_state);
            console.log("the _address_zip: "+_address_zip);
            console.log("the _address_country: "+_address_country);

            handleManualChange('address', _address_line)
            handleManualChange('city', _address_city)
            handleManualChange('state', _address_state)
            handleManualChange('zip', _address_zip)
            handleManualChange('country', _address_country)

        });

    }, [PA_GlobalObject.Planning.id]);

    const formatName = (str) => {
        if (!str) return "";
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    // Filter AND Sort Logic for Previous Attendees
    const filteredPrevious = useMemo(() => {
        // 1. Format names to Title Case
        let result = dbContacts.map(person => ({
            ...person,
            fname: formatName(person.fname),
            lname: formatName(person.lname)
        }));

        // 2. Filter based on search query
        if (searchQuery.trim()) {
            result = result.filter(person => {
                const compareName = searchCriteria === 'first' ? person.fname : person.lname;
                return (compareName || "").toLowerCase().startsWith(searchQuery.toLowerCase());
            });
        }

        // 3. Dual-Sorting Logic
        return result.sort((a, b) => {
            const firstA = a.fname || "";
            const firstB = b.fname || "";
            const lastA = a.lname || "";
            const lastB = b.lname || "";

            if (searchCriteria === 'first') {
                // Sort by First Name, then Last Name
                return firstA.localeCompare(firstB) || lastA.localeCompare(lastB);
            } else {
                // Sort by Last Name, then First Name
                return lastA.localeCompare(lastB) || firstA.localeCompare(firstB);
            }
        });
    }, [dbContacts, searchQuery, searchCriteria]);

    // Grouped Summary Logic - Sorted Alphabetically Within Groups
    const groupedAttendees = useMemo(() => {
        console.log("selectedAttendees : ", selectedAttendees);

        // 1. Group the attendees by their source category
        const groups = selectedAttendees.reduce((acc, attendee) => {
            const group = attendee.source || 'Previous';
            if (!acc[group]) acc[group] = [];
            acc[group].push(attendee);
            return acc;
        }, {});

        // 2. Alphabetize the attendees within each group
        Object.keys(groups).forEach(groupKey => {
            groups[groupKey].sort((a, b) => {
                // Normalize names for comparison (checking both fname and base name property)
                const nameA = (a.fname || a.name || "").toLowerCase();
                const nameB = (b.fname || b.name || "").toLowerCase();

                // Primary sort by name string
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;

                // Secondary fallback sort by last name if first names match
                const lastA = (a.lname || "").toLowerCase();
                const lastB = (b.lname || "").toLowerCase();
                return lastA.localeCompare(lastB);
            });
        });

        return groups;
    }, [selectedAttendees]);

    const handleToggleAttendee = (attendee, group) => {
        const exists = selectedAttendees.find(a => a.id === attendee.id);

        if (exists) {
            setSelectedAttendees(selectedAttendees.filter(a => a.id !== attendee.id));
        } else {
            // 1. Create a fresh instance of your class
            const newEntry = new PAAttendee(attendee.fname, attendee.lname, attendee.email);

            // 2. Hydrate the instance with the existing data
            // 3. Force the 'source' and 'isSample' based on the selection context
            Object.assign(newEntry, {
                ...attendee,
                id: attendee.id, // Keep their real DB id if they have one
                source: group,
                welcome_requested: sendWelcome,
                welcome_message: sendWelcome ? customMessage : "",
                rsvp_requested: sendRSVP,
                isSample: group === "Sample List",
            });

            setSelectedAttendees([...selectedAttendees, newEntry]);
        }
    };

    const handleAccessDeviceContacts = async () => {
        if (!('contacts' in navigator && 'ContactsManager' in window)) {
            alert("The Contact Picker API is not supported or is blocked by your browser security configuration.");
            return;
        }

        try {
            const supportedProperties = await navigator.contacts.getProperties();
            const fieldsToRequest = [];
            if (supportedProperties.includes('name')) fieldsToRequest.push('name');
            if (supportedProperties.includes('email')) fieldsToRequest.push('email');
            if (supportedProperties.includes('tel')) fieldsToRequest.push('tel');

            // Fire native Android dialog
            const contacts = await navigator.contacts.select(fieldsToRequest, { multiple: true });

            if (contacts.length === 0) return; // User canceled

            contacts.forEach(deviceContact => {
                const rawName = deviceContact.name?.[0] || "";
                const rawEmail = deviceContact.email?.[0] || "";
                const rawPhone = deviceContact.tel?.[0] || "";

                let firstName = rawName;
                let lastName = "";
                if (rawName.includes(" ")) {
                    const parts = rawName.split(" ");
                    firstName = parts[0];
                    lastName = parts.slice(1).join(" ");
                }

                // Generate unique temporary id for staging
                const contactId = `device-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

                const newContactEntry = {
                    id: contactId,
                    fname: firstName,
                    lname: lastName,
                    email: rawEmail,
                    phone: rawPhone,
                    source: 'Contacts'
                };

                // Push into your staging list
                handleToggleAttendee(newContactEntry, 'Contacts');
            });

        } catch (err) {
            console.error("Native Contact Picker Error:", err);
            alert(`Could not read contacts: ${err.message}`);
        }
    };


    // Function to handle file change and set the avatar
    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // 2MB Limit (2 * 1024 * 1024 bytes)
        const MAX_FILE_SIZE = 4 * 1024 * 1024;

        if (file.size > MAX_FILE_SIZE) {
            alert(`File is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Please select an image under 4MB.`);
            // Reset the input so the user can try again
            event.target.value = "";
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64data = reader.result;

            handleManualChange("avatar", {
                preview: base64data,
                name: file.name,
                type: file.type,
                size: file.size,
                hasBeenUpdated: true,
            });
        };

        reader.readAsDataURL(file);
    };

    const parseV2Address = (place) => {
        let _address_line = "", _address_city = "", _address_state = "", _address_zip = "", _address_country = "";
        const components = place.addressComponents || [];
        const _country_comp = components.find(c => c.types.includes("country"));
        const _country_code = _country_comp?.shortText || "";

        components.forEach((component) => {
            const types = component.types;
            const val = component.longText;
            if (types.includes("street_number")) _address_line = val;
            else if (types.includes("route")) _address_line = _address_line ? `${_address_line} ${val}` : val;
            else if (types.includes("locality")) _address_city = val;
            else if (types.includes("administrative_area_level_1")) _address_state = (_country_code === "US") ? component.shortText : val;
            else if (types.includes("postal_code")) _address_zip = val;
            else if (types.includes("country")) _address_country = component.shortText;
        });

        if (!_address_line && place.formattedAddress) {
            _address_line = place.formattedAddress.split(',')[0].trim();
        }
        return { _address_line, _address_city, _address_state, _address_zip, _address_country };
    };
    const handleUniversalSelect = (place, isBusiness) => {
        console.log("the selected Place 111: ", place.toJSON());

        const parsedAddr = parseV2Address(place);
        console.log("the parsedAddr 111: ", parsedAddr);

        handleManualChange('address', parsedAddr._address_line);
        handleManualChange('city', parsedAddr._address_city);
        handleManualChange('state', parsedAddr._address_state);
        handleManualChange('zip', parsedAddr._address_zip);
        handleManualChange('country', parsedAddr._address_country);

    };
    const {
        containerRef: locationRef,
        initAutocomplete: initUniversal,
        autocompleteInstance
    } = useGoogleAutocompleteUniversal(handleUniversalSelect);

    useEffect(() => {
        // try to (re)initialize/append the autocomplete
        if (locationRef.current) {
            initUniversal();
        }
    }, [activeTab, locationRef.current]);

    const handleAddManual = () => {
        if (manualEntry.fname && manualEntry.lname) {
            // 1. Create the instance
            const attendee = new PAAttendee(manualEntry.fname, manualEntry.lname, manualEntry.email);

            // 2. Hydrate + Preserve the Source
            // We explicitly add 'source' here so the Summary List and Server know what's up
            Object.assign(attendee, {
                ...manualEntry,
                source: 'Manual Entry',
                welcome_requested: sendWelcome,
                welcome_message: sendWelcome ? customMessage : "",
                rsvp_requested: sendRSVP,
            });

            if (editingId) {
                // UPDATE existing entry
                setSelectedAttendees(prev => prev.map(item => {
                    if (item.id === editingId) {
                        // 1. Maintain the class instance (so it keeps methods/budget/etc.)
                        // 2. Spread the manualEntry state into it
                        // 3. Manually update the calculated 'name' and notification fields
                        return Object.assign(item, {
                            ...manualEntry,
                            welcome_requested: sendWelcome,
                            welcome_message: sendWelcome ? customMessage : "",
                            rsvp_requested: sendRSVP,
                            last_mod: Date.now() // Track when it was changed
                        });
                    }
                    return item;
                }));
                setEditingId(null); // Clear edit mode
            } else {
                // ADD new entry
                setSelectedAttendees(prev => [...prev, attendee]);
            }

            // Reset form
            setManualEntry({
                avatar: null, fname: '', lname: '', nickname: '', isNicknameDirty: false,
                email: '', phone_prefix: '+1', phone: '', address: '', city: '',
                state: '', zip: '', country: 'US', dob: '', language: 'en', preferred_airport: ''
            });
        }
    };
    const handleEditManual = (person) => {
        // CHANGE: Check the 'source' property we tagged during creation
        if (person.source === 'Manual Entry') {
            setEditingId(person.id);

            // 1. Populate the form state with the person's current data
            // This spreads all PAAttendee properties into your manualEntry state
            setManualEntry({
                ...person,
                isNicknameDirty: (person.nickname || '') !== (person.fname || '')
            });

            // 2. Switch to the Manual Entry Tab (Tab 3)
            setActiveTab(3);


        }
    };
    const handleImportContacts = async () => {
        if (debugOverride && !('contacts' in navigator)) {
            const uniqueId = `sim-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const mockImport = [
                { id: uniqueId, name: 'Simulated Contact', email: `simulated-${uniqueId}@phone.com`, avatar: 'S', source: 'Phone Contacts' }
            ];
            setSelectedAttendees(prev => [...prev, ...mockImport]);
            return;
        }

        try {
            const props = ['name', 'email'];
            const opts = { multiple: true };
            const contacts = await navigator.contacts.select(props, opts);

            const imported = contacts.map(c => ({
                id: `contact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                name: c.name?.[0] || 'Unknown Contact',
                email: c.email?.[0] || '',
                avatar: (c.name?.[0] || 'U').charAt(0).toUpperCase(),
                source: 'Phone Contacts'
            })).filter(c => c.email);

            setSelectedAttendees(prev => [...prev, ...imported]);
        } catch (err) {
            console.error("Contacts picker error:", err);
        }
    };

    const removeAttendee = (id) => {
        setSelectedAttendees(selectedAttendees.filter(a => a.id !== id));
    };

    const isSelected = (id) => !!selectedAttendees.find(a => a.id === id);

    const [isSaving, setIsSaving] = useState(false);
    const saveEventAttendees = async () => {
        setIsSaving(true);

        try {
            // 1. Categorize data for the Global Object & Server
            const manualEntries = selectedAttendees.filter(a => a.source === 'Manual Entry');
            const androidEntries = selectedAttendees.filter(a => a.source === 'Contacts');
            const sampleEntries = selectedAttendees.filter(a => a.source === 'Sample List');
            const existingEntries = selectedAttendees.filter(a => a.source === 'Previous');

            // 2. Update the Global Planning Object (For BOTH Guests and Auth Users)
            // This ensures the UI reflects the changes immediately
            const allNewAttendees = [...existingEntries, ...androidEntries, ...sampleEntries, ...manualEntries];

            console.log("allNewAttendees : ", allNewAttendees);

            // Assuming we want to append these to the existing planning list
            set_PA_GlobalObject((prev) => {
                const existingAttendees = prev.Planning?.attendees || [];

                // Every item in selectedAttendees is already a PAAttendee instance
                const updatedNewAttendees = allNewAttendees.map(attendee => ({
                    ...attendee,
                    // Assign role: Admin if it's the account holder, otherwise Guest
                    role: attendee.id === Account?.id ? 'admin' : (attendee.role || 'guest'),
                    last_mod: Date.now()
                }));

                // Merge and avoid duplicates based on ID
                const uniqueAttendees = [
                    ...existingAttendees,
                    ...updatedNewAttendees.filter(
                        newAttendee => !existingAttendees.some(existing => existing.id === newAttendee.id)
                    ),
                ];

                // Ensure at least one admin exists
                const hasAdmin = uniqueAttendees.some(att =>
                    ['admin', 'master_admin'].includes(att.role)
                );

                if (!hasAdmin && uniqueAttendees.length > 0) {
                    uniqueAttendees[0].role = 'admin';
                }

                // ─── SYNCHRONIZE HOST ARRAY WITH ADMINS ───────────────────
                // Filter out any user IDs matching an admin status role
                const updatedHosts = uniqueAttendees
                    .filter(att => ['admin', 'master_admin'].includes(att.role))
                    .map(att => att.id);


                return {
                    ...prev, // Keep top level fields
                    Planning: {
                        ...prev.Planning,
                        attendees: uniqueAttendees,
                        host: updatedHosts
                    },
                };
            });


            if (PA_GlobalObject.Planning.status !== 10) {
                const payload = {
                    eventId: PA_GlobalObject.Planning.id,
                    existingAttendeeIds: existingEntries.map(a => a.id),
                    externalContacts: androidEntries,
                    newManualAttendees: manualEntries,
                    notificationSettings: {
                        sendRSVP,
                        sendWelcome,
                        customMessage: sendWelcome ? customMessage : ""
                    }
                };


                //send new attendees to server
                const _sendString = {};
                _sendString.action = "E_addEventAttendees_v2";
                _sendString.event_id = PA_GlobalObject.Planning.id;
                _sendString.attendees = payload;

                console.log("_sendString : ", _sendString);
                sendSocketMessage(_sendString);

            } else {
                // Guest Flow
                console.log("Added to your local plan! Sign in to save permanently.");
            }

            // 4. Wrap up UI
            onClose();

        } catch (error) {
            console.error("Save Error:", error);
        } finally {
            setIsSaving(false);
        }
    };



    return (

        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    minWidth: 350, // or '350px'
                    width: '100%', // allows it to scale on smaller screens
                },
            }}
        >
            {/* --- GLASSMORPHIC ATTENDEES HEADER --- */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(12px)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.6)',
                    boxShadow: '0 4px 24px -6px rgba(0,0,0,0.06)',
                    borderRadius: isMobile ? 0 : '16px 16px 0 0', // Blends seamlessly into top dialog curves
                    position: 'relative',
                    zIndex: 10,
                    flexShrink: 0
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    {/* Title Area & Dynamic Count Badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                        <Box sx={{
                            background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)', // Vibrant Indigo-Purple Attendee Gradient
                            p: 0.9,
                            borderRadius: '10px',
                            display: 'flex',
                            boxShadow: '0 4px 10px rgba(99, 102, 241, 0.3)'
                        }}>
                            <i className="icon-group" style={{ color: 'white', fontSize: '16px' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem', letterSpacing: -0.4, whiteSpace: 'nowrap' }}>
                                    Add Event Attendees
                                </Typography>
                                <Box sx={{
                                    bgcolor: selectedAttendees.length > 0 ? '#10b981' : '#cbd5e1',
                                    color: 'white',
                                    fontSize: '0.68rem',
                                    fontWeight: 900,
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: '20px',
                                    transition: 'all 0.3s ease',
                                    boxShadow: selectedAttendees.length > 0 ? '0 2px 8px rgba(16, 185, 129, 0.3)' : 'none'
                                }}>
                                    {selectedAttendees.length} Staged
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.1 }}>
                                <i className="icon-people" style={{ color: '#6366f1', fontSize: '10px' }} />
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.65rem', letterSpacing: 0.1, whiteSpace: 'nowrap' }}>
                                    Manage Guest List & Automated Invitations
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Sleek, Premium Dismiss Button */}
                    <IconButton
                        size="small"
                        onClick={onClose}
                        sx={{
                            color: '#94a3b8',
                            borderRadius: '8px',
                            width: '32px',
                            height: '32px',
                            border: '1px solid #e2e8f0',
                            bgcolor: 'white',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                                bgcolor: '#fef2f2',
                                color: '#ef4444',
                                borderColor: '#fca5a5',
                                transform: 'rotate(90deg)'
                            }
                        }}
                    >
                        <i className="icon-cross" style={{ fontSize: '11px', fontWeight: 'bold' }} />
                    </IconButton>

                </Box>
            </Paper>

            {/* Dialog Content */}
            <DialogContent
                sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    overflow: 'hidden',
                    height: isMobile ? 'auto' : '70vh', // Forces the Dialog to a specific height
                    maxHeight: '800px'
                }}
            >
                {/* Main Selection Wrapper */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: isMobile ? '100%' : '60%',
                        height: '100%', minHeight: 0
                    }}
                >
                    {/* --- GLASSMORPHIC CONTROL TABS --- */}
                    <Box
                        sx={{
                            p: 1,
                            background: 'rgba(248, 250, 252, 0.6)', // Transparent slate mesh
                            backdropFilter: 'blur(8px)',
                            borderBottom: '1px solid #e2e8f0',
                            flexShrink: 0,
                        }}
                    >
                        <Tabs
                            value={activeTab}
                            onChange={(e, v) => setActiveTab(v)}
                            variant="fullWidth"
                            TabIndicatorProps={{
                                style: {
                                    height: '3px',
                                    borderRadius: '3px',
                                    background: 'linear-gradient(90deg, #6366f1 0%, #a855f7 100%)', // Connected color bridge
                                }
                            }}
                            sx={{
                                minHeight: '44px',
                                '& .MuiTabs-flexContainer': {
                                    gap: 0.5, // Subtle structural gap between glass segments
                                }
                            }}
                        >
                            {/* Tab 1: Previous */}
                            <Tab
                                icon={<HistoryIcon style={{ fontSize: '18px', transition: 'transform 0.2s' }} />}
                                iconPosition="start"
                                label="Previous"
                                sx={{
                                    minHeight: '44px',
                                    textTransform: 'none',
                                    fontWeight: activeTab === 0 ? 800 : 600,
                                    fontSize: '0.82rem',
                                    letterSpacing: -0.2,
                                    borderRadius: '8px',
                                    color: '#64748b',
                                    transition: 'all 0.2s ease',
                                    '&.Mui-selected': {
                                        color: '#6366f1',
                                        bgcolor: 'white',
                                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.06)',
                                        '& svg': { transform: 'scale(1.1)' }
                                    },
                                    '&:hover:not(.Mui-selected)': {
                                        bgcolor: 'rgba(255, 255, 255, 0.4)',
                                        color: '#475569'
                                    }
                                }}
                            />

                            {/* Tab 2: Sample */}
                            <Tab
                                icon={<GroupIcon style={{ fontSize: '18px', transition: 'transform 0.2s' }} />}
                                iconPosition="start"
                                label="Sample"
                                sx={{
                                    minHeight: '44px',
                                    textTransform: 'none',
                                    fontWeight: activeTab === 1 ? 800 : 600,
                                    fontSize: '0.82rem',
                                    letterSpacing: -0.2,
                                    borderRadius: '8px',
                                    color: '#64748b',
                                    transition: 'all 0.2s ease',
                                    '&.Mui-selected': {
                                        color: '#6366f1',
                                        bgcolor: 'white',
                                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.06)',
                                        '& svg': { transform: 'scale(1.1)' }
                                    },
                                    '&:hover:not(.Mui-selected)': {
                                        bgcolor: 'rgba(255, 255, 255, 0.4)',
                                        color: '#475569'
                                    }
                                }}
                            />

                            {/* Tab 3: Contacts */}
                            <Tab
                                icon={<ContactPhoneIcon style={{ fontSize: '18px', transition: 'transform 0.2s' }} />}
                                iconPosition="start"
                                label="Contacts"
                                sx={{
                                    minHeight: '44px',
                                    textTransform: 'none',
                                    fontWeight: activeTab === 2 ? 800 : 600,
                                    fontSize: '0.82rem',
                                    letterSpacing: -0.2,
                                    borderRadius: '8px',
                                    color: '#64748b',
                                    transition: 'all 0.2s ease',
                                    '&.Mui-selected': {
                                        color: '#6366f1',
                                        bgcolor: 'white',
                                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.06)',
                                        '& svg': { transform: 'scale(1.1)' }
                                    },
                                    '&:hover:not(.Mui-selected)': {
                                        bgcolor: 'rgba(255, 255, 255, 0.4)',
                                        color: '#475569'
                                    }
                                }}
                            />

                            {/* Tab 4: Manual */}
                            <Tab
                                icon={<EditIcon style={{ fontSize: '18px', transition: 'transform 0.2s' }} />}
                                iconPosition="start"
                                label="Manual"
                                sx={{
                                    minHeight: '44px',
                                    textTransform: 'none',
                                    fontWeight: activeTab === 3 ? 800 : 600,
                                    fontSize: '0.82rem',
                                    letterSpacing: -0.2,
                                    borderRadius: '8px',
                                    color: '#64748b',
                                    transition: 'all 0.2s ease',
                                    '&.Mui-selected': {
                                        color: '#a855f7', // Accents the custom setup tab with the purple side of the gradient
                                        bgcolor: 'white',
                                        boxShadow: '0 4px 12px rgba(168, 85, 247, 0.06)',
                                        '& svg': { transform: 'scale(1.1)' }
                                    },
                                    '&:hover:not(.Mui-selected)': {
                                        bgcolor: 'rgba(255, 255, 255, 0.4)',
                                        color: '#475569'
                                    }
                                }}
                            />
                        </Tabs>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {activeTab === 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    overflow: 'hidden', // Prevents the outer container from scrolling
                                }}
                            >
                                {!authenticated ? (
                                    <Fade in={!authenticated}>
                                        <Box
                                            sx={{
                                                p: 6,
                                                textAlign: 'center',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '100%',
                                            }}
                                        >
                                            <LockIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
                                            <Typography variant="h6" fontWeight="bold">
                                                Account Required
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Log in to see your Contacts and use the search filter.
                                            </Typography>
                                            <Button variant="contained" startIcon={<LoginIcon />}>
                                                Sign In
                                            </Button>
                                        </Box>
                                    </Fade>
                                ) : (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        minHeight: 0
                                    }}>
                                        {/* STICKY HEADER AREA */}
                                        <Box
                                            sx={{
                                                p: 2,
                                                bgcolor: '#f8fafc',
                                                borderBottom: '1px solid #e2e8f0',
                                                flexShrink: 0, // Prevents search/buttons from shrinking or scrolling away
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Search by name..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{ mb: 1.5, bgcolor: 'white' }}
                                            />
                                            <ToggleButtonGroup
                                                value={searchCriteria}
                                                exclusive
                                                fullWidth
                                                size="small"
                                                onChange={(e, v) => v && setSearchCriteria(v)}
                                            >
                                                <ToggleButton value="first" sx={{ textTransform: 'none' }}>
                                                    First Name
                                                </ToggleButton>
                                                <ToggleButton value="last" sx={{ textTransform: 'none' }}>
                                                    Last Name
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </Box>

                                        {/* SCROLLABLE LIST AREA */}
                                        <Box
                                            sx={{
                                                flex: 1,         // Takes up all remaining space
                                                overflowY: 'auto', // Enables scrolling only here
                                                minHeight: 0,    // Essential fix for flexbox overflow
                                                bgcolor: 'white'
                                            }}
                                        >
                                            <List sx={{ px: 1, py: 1 }}>
                                                {filteredPrevious.length > 0 ? (
                                                    filteredPrevious.map((person) => {
                                                        // 1. Check if this person is already an active attendee of the current event
                                                        const currentRoster = PA_GlobalObject.Planning?.attendees || [];
                                                        const isAlreadyAdded = currentRoster.some(att => att.id === person.id);
                                                        const selected = isSelected(person.id);

                                                        return (
                                                            <ListItem key={person.id} disablePadding alignItems="flex-start" sx={{ mb: 1.5 }}>
                                                                <ListItemButton
                                                                    // 2. Disable click interactions completely if already added
                                                                    disabled={isAlreadyAdded}
                                                                    onClick={() => handleToggleAttendee(person, 'Previous')}
                                                                    sx={{
                                                                        borderRadius: '12px',
                                                                        // 3. Subdued gray background vs interactive states
                                                                        bgcolor: isAlreadyAdded
                                                                            ? '#f8fafc'
                                                                            : (selected ? 'rgba(99, 102, 241, 0.08)' : '#ffffff'),
                                                                        border: '1px solid',
                                                                        borderColor: isAlreadyAdded
                                                                            ? '#e2e8f0'
                                                                            : (selected ? '#6366f1' : '#e2e8f0'),
                                                                        transition: 'all 0.2s ease',
                                                                        p: 2,
                                                                        // 4. Muted slate border for existing, colorful accent for new additions
                                                                        borderLeft: `6px solid ${
                                                                            isAlreadyAdded
                                                                                ? '#94a3b8'
                                                                                : (person.role === 'guest' ? '#6366f1' : '#f59e0b')
                                                                        }`,
                                                                        // 5. Visual opacity drop to signal a non-interactive/saved row
                                                                        opacity: isAlreadyAdded ? 0.65 : 1,
                                                                        // 6. Strip hover animations cleanly if disabled
                                                                        '&:hover': isAlreadyAdded ? {} : {
                                                                            bgcolor: '#f8fafc',
                                                                            transform: 'translateY(-2px)',
                                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                                                        },
                                                                        '&.Mui-disabled': {
                                                                            opacity: 0.65, // Forces Material-UI to respect our custom opacity
                                                                            color: 'inherit'
                                                                        }
                                                                    }}
                                                                >
                                                                    <ListItemAvatar sx={{ mt: 0.5 }}>
                                                                        <Avatar
                                                                            src={person.avatar?.preview}
                                                                            sx={{
                                                                                width: 48,
                                                                                height: 48,
                                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                                                border: '2px solid white',
                                                                                // Gray scale avatar filter if already added
                                                                                filter: isAlreadyAdded ? 'grayscale(80%)' : 'none'
                                                                            }}
                                                                        >
                                                                            {person.fname.charAt(0)}
                                                                        </Avatar>
                                                                    </ListItemAvatar>

                                                                    <ListItemText
                                                                        primary={
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                                                <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', display: 'block' }}>
                                                                                    {searchCriteria === 'first' ? (
                                                                                        <>{person.fname} <span style={{ fontWeight: 400, opacity: 0.7 }}>{person.lname}</span></>
                                                                                    ) : (
                                                                                        <>{person.lname}, <span style={{ fontWeight: 400, opacity: 0.7 }}>{person.fname}</span></>
                                                                                    )}
                                                                                </Typography>
                                                                                {/* Conditional Nickname */}
                                                                                {person.nickname &&
                                                                                    person.nickname.toLowerCase() !== person.fname.toLowerCase() && (
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{
                                                                                                color: '#94a3b8',
                                                                                                fontWeight: 400,
                                                                                                fontStyle: 'italic',
                                                                                                fontSize: '0.85rem'
                                                                                            }}
                                                                                        >
                                                                                            ({formatName(person.nickname)})
                                                                                        </Typography>
                                                                                    )}
                                                                            </Box>
                                                                        }
                                                                        secondary={
                                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                                                                {person.email && (
                                                                                    <Typography variant="caption" sx={{ color: '#475569', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                        <i className="icon-envelope" style={{ fontSize: '10px' }} /> {person.email}
                                                                                    </Typography>
                                                                                )}
                                                                                {person.phone && (
                                                                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                                                        {person.phone_prefix} {person.phone}
                                                                                    </Typography>
                                                                                )}
                                                                                {(person.city || person.state) && (
                                                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                                                                                        {person.city}, {person.state}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        }
                                                                    />

                                                                    {/* Action / Meta Area */}
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between', height: '100%', ml: 2 }}>
                                                                        {isAlreadyAdded ? (
                                                                            // 7. Render a pill badge instead of a checkbox
                                                                            <Chip
                                                                                label="On Roster"
                                                                                size="small"
                                                                                variant="outlined"
                                                                                icon={<i className="icon-check" style={{ fontSize: '10px' }} />}
                                                                                sx={{
                                                                                    height: 24,
                                                                                    borderColor: '#cbd5e1',
                                                                                    color: '#64748b',
                                                                                    bgcolor: '#f1f5f9',
                                                                                    fontWeight: 700,
                                                                                    fontSize: '0.7rem',
                                                                                    textTransform: 'uppercase',
                                                                                    letterSpacing: '0.02em'
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <Checkbox
                                                                                checked={selected}
                                                                                color="primary"
                                                                                icon={<AddCircleIcon sx={{ opacity: 0.3 }} />}
                                                                                checkedIcon={<CheckCircleIcon />}
                                                                            />
                                                                        )}

                                                                        {person.events?.length > 0 && (
                                                                            <Chip
                                                                                label={`${person.events.length} ${person.events.length === 1 ? 'Event' : 'Events'}`}
                                                                                size="small"
                                                                                sx={{
                                                                                    height: 20,
                                                                                    fontSize: '0.65rem',
                                                                                    bgcolor: isAlreadyAdded ? '#e2e8f0' : '#f1f5f9',
                                                                                    color: '#475569',
                                                                                    fontWeight: 700,
                                                                                    mt: isAlreadyAdded ? 1.5 : 2
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                </ListItemButton>
                                                            </ListItem>
                                                        );
                                                    })
                                                ) : (
                                                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                                        <Typography variant="body2">No contacts found matching "{searchQuery}"</Typography>
                                                    </Box>
                                                )}
                                            </List>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {activeTab === 1 && (
                            <Fade in={activeTab === 1}>
                                <List sx={{ px: 2, py: 2 }}>
                                    <ListSubheader sx={{ bgcolor: 'white', zIndex: 10 }}>Sample Attendees</ListSubheader>
                                    {SampleAttendees.map((person) => (
                                        <ListItem key={person.id} disablePadding sx={{ mb: 1 }}>
                                            <ListItemButton
                                                onClick={() => handleToggleAttendee(person, 'Sample List')}
                                                sx={{ borderRadius: 2, bgcolor: isSelected(person.id) ? '#f0fdf4' : 'transparent' }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: isSelected(person.id) ? 'success.main' : 'grey.400' }}>
                                                        {person.avatar}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText primary={person.name} secondary={person.email} />
                                                <Checkbox edge="end" checked={isSelected(person.id)} disableRipple />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </Fade>
                        )}

                        {activeTab === 2 && (
                            <Box sx={{ height: '100%', overflowY: 'auto' }}>
                                {!canAccessContacts ? (
                                    <Fade in={!canAccessContacts}>
                                        <Box sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100%' }}>
                                            <DevicesIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
                                            <Typography variant="h6" fontWeight="bold">Device Feature</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '300px' }}>
                                                Native contact importing is only available on <strong>Android devices</strong> using the <strong>Google Chrome</strong> browser.
                                            </Typography>
                                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'left', width: '100%', maxWidth: '320px' }}>
                                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                    <InfoIcon color="action" fontSize="small" sx={{ mt: 0.3 }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Please switch to a supported mobile device to sync your address book directly with this event.
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        </Box>
                                    </Fade>
                                ) : (
                                    <Fade in={canAccessContacts}>
                                        <Box sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100%' }}>
                                            <ContactPhoneIcon sx={{ fontSize: 60, color: '#6366f1', mb: 2 }} />
                                            <Typography variant="h6">Phone Contacts</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Import guests directly from your local address book.
                                            </Typography>

                                            {/* 👇 Real Function Connected Here */}
                                            <Button
                                                variant="outlined"
                                                sx={{ px: 4, borderRadius: 10 }}
                                                onClick={handleAccessDeviceContacts}
                                            >
                                                Access Device Contacts
                                            </Button>

                                        </Box>
                                    </Fade>
                                )}
                            </Box>
                        )}

                        {activeTab === 3 && (
                            <Fade in={activeTab === 3}>
                                <Box sx={{ p: 3, bgcolor: '#f1f5f9', minHeight: '100%' }}>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#1e293b', fontWeight: 700 }}>
                                        New Attendee Profile
                                    </Typography>

                                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                                        {/* Section 1: Identity */}
                                        <Typography variant="overline" sx={{ color: '#6366f1', fontWeight: 900 }}>
                                            Identity
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                                            {/* Row 1: Select avatar */}
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                                <input
                                                    type="file"
                                                    id="file-input-new-attendee"
                                                    accept="image/*"
                                                    style={{display: 'none'}}
                                                    onChange={handleImageChange}
                                                />
                                                <Box sx={{ position: 'relative' }}>
                                                    <Avatar
                                                        src={manualEntry.avatar?.preview || ''}
                                                        sx={{
                                                            width: 72,
                                                            height: 72,
                                                            cursor: 'pointer',
                                                            border: '2px solid #6366f1',
                                                            boxShadow: '0 4px 10px rgba(99, 102, 241, 0.2)',
                                                            transition: 'filter 0.3s',
                                                            '&:hover': {
                                                                filter: 'brightness(0.8)'
                                                            }
                                                        }}
                                                        onClick={() => document.getElementById('file-input-new-attendee').click()}
                                                    >
                                                        {/* Fallback if no image is selected */}
                                                        {!manualEntry.avatar?.preview && manualEntry.fname.charAt(0)}
                                                    </Avatar>

                                                    {/* Small Edit Icon Badge */}
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        right: 0,
                                                        bgcolor: '#6366f1',
                                                        borderRadius: '50%',
                                                        width: 24,
                                                        height: 24,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: '2px solid white',
                                                        pointerEvents: 'none'
                                                    }}>
                                                        <CameraAltIcon sx={{ color: 'white', fontSize: '14px' }} />
                                                    </Box>
                                                </Box>

                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                        Profile Picture
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        Click photo to upload (JPG, PNG)
                                                    </Typography>
                                                    {manualEntry.avatar && (
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            sx={{ fontSize: '0.65rem', mt: 0.5, p: 0 }}
                                                            onClick={() => handleManualChange('avatar', null)}
                                                        >
                                                            Remove
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                            {/* Row 2: First and Last Name together */}
                                            <Box sx={{display: 'flex', gap: 2}}>
                                                <TextField
                                                    label="First Name"
                                                    size="small"
                                                    fullWidth
                                                    required
                                                    value={manualEntry.fname}
                                                    onChange={(e) => handleManualChange('fname', e.target.value)}
                                                />
                                                <TextField
                                                    label="Last Name"
                                                    size="small"
                                                    fullWidth
                                                    required
                                                    value={manualEntry.lname}
                                                    onChange={(e) => handleManualChange('lname', e.target.value)}
                                                />
                                            </Box>

                                            {/* Row 2: Nickname (Full Width) */}
                                            <TextField
                                                label={manualEntry.isNicknameDirty ? "Nickname" : "Nickname (Auto-syncs with First Name)"}
                                                size="small"
                                                fullWidth
                                                value={manualEntry.nickname}
                                                onChange={(e) => handleManualChange('nickname', e.target.value)}
                                                sx={{
                                                    '& .MuiInputLabel-root': {
                                                        fontStyle: manualEntry.isNicknameDirty ? 'normal' : 'italic'
                                                    }
                                                }}
                                            />
                                        </Box>

                                        {/* Section 2: Contact Info */}
                                        <Typography variant="overline" sx={{ color: '#6366f1', fontWeight: 900 }}>Contact</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                                            <TextField
                                                label="Email Address" size="small"
                                                fullWidth value={manualEntry.email}
                                                onChange={(e) => handleManualChange('email', e.target.value)} helperText="Required for login"
                                            />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <FormControl
                                                    fullWidth
                                                    size="small"
                                                >
                                                    <InputLabel id="demo-simple-select-standard-label">Phone Prefix</InputLabel>
                                                    <Select
                                                        labelId="demo-simple-select-standard-label"
                                                        id="demo-simple-select-standard"
                                                        value={manualEntry.phone_prefix}
                                                        onChange={(e) => handleManualChange('phone_prefix', e.target.value)}
                                                        label="Phone Prefix"
                                                    >
                                                        <MenuItem value="">
                                                            <em>None</em>
                                                        </MenuItem>
                                                        {
                                                            country_codes.map((item, index) => (
                                                                <MenuItem key={index} value={item.dialCode}>
                                                                    {item.dialCode} : {item.name}
                                                                </MenuItem>
                                                            ))
                                                        }
                                                    </Select>
                                                </FormControl>
                                                <TextField label="Phone" size="small" fullWidth
                                                           value={manualEntry.phone}
                                                           onChange={(e) => handleManualChange('phone', e.target.value)}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Section 3: Location */}
                                        <Typography variant="overline" sx={{ color: '#6366f1', fontWeight: 900 }}>Address</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                                            <Box
                                                ref={locationRef}
                                                sx={{
                                                    flex: 1,
                                                    minHeight: '40px',
                                                    width: '100%',
                                                    '& gmp-place-autocomplete': {
                                                        width: '100%',
                                                        display: 'block',
                                                        colorScheme: 'light !important',
                                                        backgroundColor: '#ffffff !important',
                                                        borderRadius: '12px !important',
                                                    }
                                                }}
                                            />
                                            <TextField
                                                fullWidth
                                                id="address"
                                                label="Street Address"
                                                value={manualEntry.address}
                                                onChange={(e) => handleManualChange('address', e.target.value)}
                                                inputRef={inputRef}
                                            />
                                            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 2 }}>
                                                <TextField
                                                    label="City" size="small" value={manualEntry.city} onChange={(e) => handleManualChange('city', e.target.value)} />
                                                <TextField
                                                    label="State" size="small" value={manualEntry.state} onChange={(e) => handleManualChange('state', e.target.value)} />
                                                <TextField
                                                    label="Zip" size="small" value={manualEntry.zip} onChange={(e) => handleManualChange('zip', e.target.value)} />
                                            </Box>
                                            <TextField
                                                label="Country" size="small" fullWidth value={manualEntry.country} onChange={(e) => handleManualChange('country', e.target.value)} />
                                        </Box>

                                        {/* Section 4: Travel & Dates */}
                                        <Typography variant="overline" sx={{ color: '#6366f1', fontWeight: 900 }}>Travel & Misc</Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2, mb: 4 }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="language-label">Preferred Language</InputLabel>
                                                <Select
                                                    labelId="language-label"
                                                    id="preferred-language-select"
                                                    value={manualEntry.language}
                                                    label="Preferred Language"
                                                    onChange={(e) => handleManualChange('language', e.target.value)}
                                                >
                                                    {supportedLanguages.map((lang) => (
                                                        <MenuItem key={lang.code} value={lang.code}>
                                                            {lang.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Autocomplete
                                                fullWidth
                                                size="small"
                                                disablePortal
                                                blurOnSelect
                                                options={_airports_list}
                                                // Add a check to ensure option exists before accessing properties
                                                getOptionLabel={(option) => option.iata ? `${option.iata} : ${option.name}` : ""}

                                                // FIND the object in the list that matches the string code in your state
                                                value={_airports_list.find(a => a.iata === manualEntry.preferred_airport) || null}

                                                onChange={(event, newValue) => {
                                                    // Store only the string (IATA code) in your state
                                                    handleManualChange('preferred_airport', newValue ? newValue.iata : '');
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Preferred Airport"/>
                                                )}
                                                // Compare the option object to the value object
                                                isOptionEqualToValue={(option, value) => option.iata === value.iata}
                                            />
                                            <TextField
                                                label="Date of Birth"
                                                size="small"
                                                type="date"
                                                InputLabelProps={{ shrink: true }}
                                                value={manualEntry.dob}
                                                onChange={(e) => handleManualChange('dob', e.target.value)}
                                            />
                                        </Box>

                                        <Button
                                            fullWidth variant="contained" size="large"
                                            startIcon={editingId ? <CheckCircleIcon /> : <AddCircleIcon />}
                                            onClick={handleAddManual}
                                            disabled={!manualEntry.fname || !manualEntry.lname}
                                            sx={{
                                                borderRadius: 3,
                                                py: 1.5,
                                                bgcolor: editingId ? '#10b981' : 'primary.main', // Green for update, Blue for add
                                                '&:hover': { bgcolor: editingId ? '#059669' : 'primary.dark' }
                                            }}
                                        >
                                            {editingId ? 'Update Attendee Details' : 'Add Attendee to Event'}
                                        </Button>
                                    </Paper>
                                </Box>
                            </Fade>
                        )}
                    </Box>
                </Box>

                {/* Right/Bottom Side: Summary Area */}
                <Box
                    sx={{
                        bgcolor: '#f8fafc',
                        p: 1,
                        borderLeft: isMobile ? 'none' : '1px solid #e2e8f0',
                        display: 'flex',
                        flexDirection: 'column',

                        flex: isMobile ? 'none' : 1,          // Stops mobile from forcing it to collapse to 0 height
                        height: 'auto',   // Allows the container to expand naturally down the page
                        minHeight: isMobile ? '280px' : 0,    // Guarantees a baseline interactive area on small screens
                        maxHeight: isMobile ? '400px' : '100%', // Prevents the summary section from swallowing the entire viewport
                        overflow: 'hidden'                    // Keeps the main wrapper tidy while the inner list scrolls
                    }}
                >
                    {/* HEADER: Always Visible */}
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexShrink: 0 }}>
                        Summary <Chip label={selectedAttendees.length} size="small" color="primary" />
                    </Typography>

                    {/* MIDDLE: Scrollable List Area */}
                    <Box sx={{
                        flex: 1,
                        overflowY: 'auto',
                        minHeight: 0, // CRITICAL: Allows this box to be smaller than its content
                        mb: 2
                    }}>
                        {selectedAttendees.length === 0 ? (
                            <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                                <PersonAddIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1 }} />
                                <Typography variant="body2">No attendees selected yet.</Typography>
                            </Box>
                        ) : (
                            <List dense subheader={<li />}>
                                {Object.keys(groupedAttendees).map((group) => (
                                    <Box key={group} sx={{ mb: 2 }}>
                                        <ListSubheader sx={{
                                            bgcolor: '#f1f5f9',
                                            lineHeight: '32px',
                                            fontWeight: 'bold',
                                            color: 'primary.main',
                                            textTransform: 'uppercase',
                                            fontSize: '0.75rem',
                                            borderRadius: 1,
                                        }}>
                                            {group}
                                        </ListSubheader>
                                        {groupedAttendees[group].map((person) => {
                                            const isManual = person.source === 'Manual Entry';
                                            const isBeingEdited = editingId === person.id;

                                            return (
                                                <ListItem
                                                    key={`${group}-${person.id}`}
                                                    disablePadding
                                                    sx={{
                                                        bgcolor: isBeingEdited ? '#eff6ff' : 'white', // Light blue highlight if editing
                                                        mb: 0.5,
                                                        borderRadius: 2,
                                                        boxShadow: isBeingEdited ? '0 0 0 2px #6366f1' : '0 1px 2px rgba(0,0,0,0.05)',
                                                        border: isManual ? '1px dashed #6366f1' : '1px solid transparent',
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                    secondaryAction={
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            onClick={() => {
                                                                if(isBeingEdited) setEditingId(null); // Clear edit mode if deleted
                                                                removeAttendee(person.id);
                                                            }}
                                                            color="error"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    }
                                                >
                                                    <ListItemButton
                                                        onClick={() => isManual && handleEditManual(person)}
                                                        disabled={!isManual}
                                                        sx={{
                                                            borderRadius: 2,
                                                            px: 2,
                                                            py: 1,
                                                            '&.Mui-disabled': { opacity: 1, cursor: 'default' }
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={
                                                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                                        {formatName(person.fname || person.name)}{' '}
                                                                        <span style={{ fontWeight: 400, color: '#64748b' }}>
                                                                            {formatName(person.lname || '')}
                                                                        </span>
                                                                    </Typography>

                                                                    {person.nickname && person.nickname.toLowerCase() !== (person.fname || '').toLowerCase() && (
                                                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.75rem' }}>
                                                                            ({formatName(person.nickname)})
                                                                        </Typography>
                                                                    )}

                                                                    {isBeingEdited && (
                                                                        <Chip label="EDITING" size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem', ml: 1 }} />
                                                                    )}
                                                                </Box>
                                                            }
                                                            secondary={
                                                                <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#64748b', noWrap: true }}>
                                                                        {person.email || 'No email provided'}
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            );
                                        })}
                                    </Box>
                                ))}
                            </List>
                        )}
                    </Box>

                    {/* BOTTOM: Sticky Notification Area */}
                    <Box sx={{ flexShrink: 0, pt: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', position: 'relative', overflow: 'hidden' }}>

                        {!authenticated ? (
                            /* FUN & PROFESSIONAL GUEST PLACEHOLDER */
                            <Box
                                sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
                                    borderRadius: 2,
                                    border: '1px dashed #cbd5e1',
                                    m: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                                    <MarkEmailReadIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                    <NotificationsActiveIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569' }}>
                                    Unlock Automated Invites
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', maxWidth: '280px', mb: 1 }}>
                                    Sign in to automatically send RSVP links and custom welcome messages to your guests.
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                    sx={{
                                        borderRadius: 5,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        px: 3,
                                        borderWidth: 2,
                                        '&:hover': { borderWidth: 2 }
                                    }}
                                    // Link this to your Auth/Login logic
                                    onClick={() => console.log("Redirect to Login")}
                                >
                                    Create Account to Enable
                                </Button>
                            </Box>
                        ) : (
                            /* AUTHENTICATED NOTIFICATION SETTINGS */
                            <>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', px: 1 }}>
                                    Notification Settings
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 1 }}>
                                    <FormControlLabel
                                        control={<Switch size="small" checked={sendRSVP} onChange={(e) => setSendRSVP(e.target.checked)} />}
                                        label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Request RSVP</Typography>}
                                    />
                                    <FormControlLabel
                                        control={<Switch size="small" checked={sendWelcome} onChange={(e) => setSendWelcome(e.target.checked)} />}
                                        label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Welcome Message</Typography>}
                                    />
                                </Box>

                                {sendWelcome && (
                                    <Grow in={sendWelcome}>
                                        <Box sx={{ mt: 2, mb: 1, px: 1, position: 'relative' }}>
                                            <Box sx={{
                                                width: 0, height: 0,
                                                borderLeft: '10px solid transparent',
                                                borderRight: '10px solid transparent',
                                                borderBottom: '10px solid #ffffff',
                                                ml: 3, mb: -0.5
                                            }} />

                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    borderRadius: '16px',
                                                    bgcolor: '#ffffff',
                                                    border: '1px solid #e2e8f0',
                                                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.08)'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                    <AutoAwesomeIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        Personalized Greeting
                                                    </Typography>
                                                </Box>

                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    placeholder="e.g. We are so excited to have you join us at the event..."
                                                    variant="standard"
                                                    value={customMessage}
                                                    onChange={(e) => setCustomMessage(e.target.value)}
                                                    InputProps={{
                                                        disableUnderline: true,
                                                        sx: {
                                                            fontSize: '0.9rem',
                                                            lineHeight: 1.5,
                                                            color: '#1e293b',
                                                            '&::placeholder': { color: '#94a3b8', fontStyle: 'italic' }
                                                        }
                                                    }}
                                                />

                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                                    <Typography variant="caption" sx={{ color: customMessage.length > 160 ? '#ef4444' : '#94a3b8', fontSize: '0.65rem' }}>
                                                        {customMessage.length} characters
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        </Box>
                                    </Grow>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </DialogContent>

            {/* --- GLASSMORPHIC FOOTER ACTIONS --- */}
            <DialogActions
                sx={{
                    p: 2,
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(16px)',
                    borderTop: '1px solid rgba(226, 232, 240, 0.8)', // Crisp division hairline
                    boxShadow: '0 -4px 20px -6px rgba(0,0,0,0.03)', // Upward soft lighting drop shadow
                    gap: 1,
                    justifyContent: 'flex-end',
                    borderRadius: isMobile ? 0 : '0 0 16px 16px', // Finishes off the bottom curve elegantly
                }}
            >
                {/* Cancel/Dismiss Action */}
                <Button
                    onClick={onClose}
                    variant="text"
                    sx={{
                        color: '#64748b',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        textTransform: 'none',
                        borderRadius: '10px',
                        px: 2.5,
                        height: '38px',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                            bgcolor: '#f1f5f9',
                            color: '#334155'
                        }
                    }}
                >
                    Cancel
                </Button>

                {/* Primary Commit Action */}
                <Button
                    onClick={saveEventAttendees}
                    variant="contained"
                    disabled={isSaving || selectedAttendees.length === 0}
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <i className="icon-check" style={{ fontSize: '12px', fontWeight: 900 }} />}
                    sx={{
                        bgcolor: '#10b981', // Matches your "Save/Commit" Emerald Green theme signature
                        color: 'white',
                        fontWeight: 800,
                        fontSize: '0.82rem',
                        textTransform: 'none',
                        borderRadius: '10px',
                        px: 3,
                        height: '38px',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            bgcolor: '#059669',
                            boxShadow: '0 6px 16px rgba(16, 185, 129, 0.35)',
                            transform: 'translateY(-1px)'
                        },
                        '&:active': {
                            transform: 'translateY(0px)'
                        },
                        '&:disabled': {
                            bgcolor: '#cbd5e1',
                            color: '#94a3b8',
                            boxShadow: 'none'
                        }
                    }}
                >
                    {isSaving ? 'Saving Changes...' : `Save ${selectedAttendees.length} ${selectedAttendees.length === 1 ? 'Attendee' : 'Attendees'}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AttendeesAddDialog;
